import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from "@material-ui/core";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import ImageIcon from "@material-ui/icons/Image";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import InputField from "components/FormFields/InputField";
import React from "react";
import Dropzone from "react-dropzone";

export interface ExamQuestionCardProps {
  question: any;
  index: number;
  mode: "edit" | "view" | "test";
  control?: any;
  isEssay?: boolean;
  // Edit callbacks
  onChangeQuestionContent?: (val: string) => void;
  onChangePoint?: (val: string) => void;
  onChangeOptionValue?: (val: string, optionIndex: number) => void;
  onSelectRightAnswer?: (optionIndex: number) => void;
  onAddAnswer?: () => void;
  onDeleteAnswer?: (optionIndex: number) => void;
  onCopyQuestion?: () => void;
  onDeleteQuestion?: () => void;
  onAddPicture?: (files: any) => void;
  onDeletePicture?: (pictureIndex: number) => void;
  // Test/View mode props
  selectedAnswerIndex?: number;
  onSelectTestAnswer?: (optionIndex: number) => void;
}

const useStyles = makeStyles((theme) => ({
  examCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    border: "1px solid #E2E8F0",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.04)",
    padding: 24,
    marginBottom: 20,
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      borderColor: "#93C5FD",
      boxShadow: "0 8px 20px rgba(19, 123, 189, 0.08)",
    },
  },
  questionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: "1px solid #F1F5F9",
    flexWrap: "wrap",
    gap: 12,
  },
  badgeNumber: {
    backgroundColor: "#EEF2FF",
    color: "#1D4ED8",
    fontWeight: 800,
    fontSize: "0.9rem",
    padding: "6px 14px",
    borderRadius: 8,
    display: "inline-flex",
    alignItems: "center",
    boxShadow: "0 2px 6px rgba(29, 78, 216, 0.12)",
  },
  optionRow: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#F8FAFC",
    borderRadius: 12,
    padding: "10px 16px",
    marginTop: 10,
    border: "1px solid #E2E8F0",
    transition: "all 0.2s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "#F1F5F9",
      borderColor: "#CBD5E1",
    },
  },
  optionRowSelected: {
    backgroundColor: "#ECFDF5",
    borderColor: "#A7F3D0",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.12)",
  },
  optionRowTestSelected: {
    backgroundColor: "#F0F9FF",
    borderColor: "#38BDF8",
    boxShadow: "0 2px 8px rgba(56, 189, 248, 0.15)",
  },
  optionBadge: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: "#E2E8F0",
    color: "#475569",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "0.8rem",
    marginRight: 12,
    flexShrink: 0,
  },
  btnAddAnswer: {
    color: "#2563EB",
    fontWeight: 700,
    fontSize: "0.85rem",
    textTransform: "none",
    marginTop: 12,
    borderRadius: 8,
    padding: "6px 14px",
    backgroundColor: "#EFF6FF",
    border: "1px dashed #BFDBFE",
    "&:hover": {
      backgroundColor: "#DBEAFE",
    },
  },
  btnDropzone: {
    border: "1px dashed #94A3B8",
    backgroundColor: "#F8FAFC",
    padding: "6px 14px",
    borderRadius: 10,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    color: "#475569",
    fontWeight: 600,
    fontSize: "0.8rem",
    transition: "all 0.2s ease",
    "&:hover": {
      color: "#2563EB",
      borderColor: "#2563EB",
      backgroundColor: "#EFF6FF",
    },
  },
  imagePreviewCard: {
    position: "relative",
    borderRadius: 12,
    overflow: "hidden",
    border: "1px solid #E2E8F0",
    backgroundColor: "#000",
  },
  closeImgBtn: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 10,
    backgroundColor: "rgba(15, 23, 42, 0.7)",
    color: "#FFFFFF",
    padding: 4,
    "&:hover": {
      backgroundColor: "#EF4444",
    },
  },
}));

export default function ExamQuestionCard({
  question,
  index,
  mode = "view",
  control,
  isEssay = false,
  onChangeQuestionContent,
  onChangePoint,
  onChangeOptionValue,
  onSelectRightAnswer,
  onAddAnswer,
  onDeleteAnswer,
  onCopyQuestion,
  onDeleteQuestion,
  onAddPicture,
  onDeletePicture,
  selectedAnswerIndex,
  onSelectTestAnswer,
}: ExamQuestionCardProps) {
  const classes = useStyles();

  const getOptionLetter = (idx: number) => String.fromCharCode(65 + idx);

  const images = question?.imageFileIDList || question?.imageFileList || [];
  const answers = question?.answers || [];

  return (
    <Paper id={`question-${index + 1}`} className={classes.examCard} elevation={0}>
      {/* Header Bar */}
      <Box className={classes.questionHeader}>
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          <span className={classes.badgeNumber}>Câu {index + 1}</span>
          <Chip
            label={isEssay ? "Tự luận" : "Trắc nghiệm"}
            size="small"
            style={{
              backgroundColor: isEssay ? "#F5F3FF" : "#EFF6FF",
              color: isEssay ? "#7C3AED" : "#2563EB",
              fontWeight: 700,
              fontSize: "0.75rem",
            }}
          />
        </Box>

        <Box display="flex" alignItems="center" style={{ gap: 12 }}>
          {mode === "edit" ? (
            <>
              <Box style={{ width: 100 }}>
                <InputField
                  name={`point-${index}`}
                  control={control}
                  defaultValue={question.point}
                  label="Điểm số"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onChangePoint && onChangePoint(event.target.value)
                  }
                />
              </Box>

              {onAddPicture && (
                <Dropzone onDrop={(files: any) => onAddPicture(files)}>
                  {({ getRootProps, getInputProps }) => (
                    <div {...getRootProps()} className={classes.btnDropzone}>
                      <ImageIcon style={{ fontSize: 16 }} />
                      <span>Ảnh</span>
                      <input {...getInputProps()} accept="image/*" />
                    </div>
                  )}
                </Dropzone>
              )}

              {onCopyQuestion && (
                <Tooltip title="Nhân bản câu hỏi">
                  <IconButton size="small" onClick={onCopyQuestion} style={{ color: "#475569" }}>
                    <FileCopyIcon style={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}

              {onDeleteQuestion && (
                <Tooltip title="Xóa câu hỏi">
                  <IconButton size="small" onClick={onDeleteQuestion} style={{ color: "#EF4444" }}>
                    <DeleteOutlineIcon style={{ fontSize: 20 }} />
                  </IconButton>
                </Tooltip>
              )}
            </>
          ) : (
            question.point !== undefined && (
              <Chip
                label={`${question.point || 0} điểm`}
                size="small"
                style={{ backgroundColor: "#FEF3C7", color: "#92400E", fontWeight: 700 }}
              />
            )
          )}
        </Box>
      </Box>

      {/* Question Content */}
      <Box mb={2}>
        {mode === "edit" ? (
          <InputField
            control={control}
            name={`questions-${index}`}
            label="Nội dung câu hỏi *"
            defaultValue={question.questionContent || question.content}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onChangeQuestionContent && onChangeQuestionContent(event.target.value)
            }
            isShowLabel={true}
            rows={2}
            multiline
          />
        ) : (
          <Typography style={{ fontSize: 16, fontWeight: 600, color: "#0F172A", lineHeight: 1.5 }}>
            {question.questionContent || question.content}
          </Typography>
        )}
      </Box>

      {/* Image Gallery */}
      {images.length > 0 && (
        <Box display="flex" flexWrap="wrap" style={{ gap: 12, marginBottom: 16 }}>
          {images.map((x: any, idxImg: number) => (
            <Box key={x.id || idxImg} className={classes.imagePreviewCard} style={{ maxWidth: 320 }}>
              {mode === "edit" && onDeletePicture && (
                <IconButton
                  size="small"
                  className={classes.closeImgBtn}
                  onClick={() => onDeletePicture(idxImg)}
                >
                  <CloseIcon style={{ fontSize: 16 }} />
                </IconButton>
              )}
              <img
                style={{
                  width: "100%",
                  maxHeight: 250,
                  objectFit: "contain",
                  display: "block",
                }}
                src={x?.url ?? x?.imageFile?.url}
                alt="Question media"
              />
            </Box>
          ))}
        </Box>
      )}

      {/* Answers Section */}
      {isEssay ? (
        <Box mt={1}>
          {mode === "edit" ? (
            <InputField
              label="Đáp án mẫu / Hướng dẫn chấm"
              name={`answerContent-${index}-0`}
              defaultValue={answers[0]?.answerContent || answers[0]?.content}
              control={control}
              rows={3}
              multiline
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                onChangeOptionValue && onChangeOptionValue(event.target.value, 0)
              }
            />
          ) : (
            <Typography variant="body2" style={{ color: "#334155", fontStyle: "italic" }}>
              {answers[0]?.answerContent || answers[0]?.content || "Chưa có đáp án mẫu"}
            </Typography>
          )}
        </Box>
      ) : (
        <RadioGroup name={`radios-${index}`} aria-label={`radios-${index}`}>
          {answers.map((answer: any, optionIdx: number) => {
            const answerText = answer.answerContent ?? answer.content;
            const isRightAnswer = Boolean(answer.isRightAnswer);
            const isTestSelected = selectedAnswerIndex === optionIdx;

            if (mode === "edit") {
              return (
                <div
                  key={optionIdx}
                  className={clsx(classes.optionRow, isRightAnswer && classes.optionRowSelected)}
                >
                  <span className={classes.optionBadge}>{getOptionLetter(optionIdx)}</span>

                  <FormControlLabel
                    style={{ margin: 0 }}
                    control={
                      <Radio
                        checked={isRightAnswer}
                        onChange={() => onSelectRightAnswer && onSelectRightAnswer(optionIdx)}
                        color="primary"
                        style={{ padding: 6 }}
                      />
                    }
                    label=""
                  />

                  <Box flex={1} ml={1}>
                    <InputField
                      label={`Lựa chọn ${getOptionLetter(optionIdx)} ${isRightAnswer ? "(Đáp án đúng ✓)" : ""}`}
                      name={`answerContent-${index}-${optionIdx}`}
                      defaultValue={answerText}
                      control={control}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onChangeOptionValue && onChangeOptionValue(event.target.value, optionIdx)
                      }
                    />
                  </Box>

                  {onDeleteAnswer && (
                    <Tooltip title="Xóa lựa chọn này">
                      <IconButton
                        size="small"
                        onClick={() => onDeleteAnswer(optionIdx)}
                        style={{ marginLeft: 8, color: "#94A3B8" }}
                      >
                        <CloseIcon style={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              );
            }

            if (mode === "view") {
              return (
                <div
                  key={optionIdx}
                  className={clsx(classes.optionRow, isRightAnswer && classes.optionRowSelected)}
                >
                  <span className={classes.optionBadge}>{getOptionLetter(optionIdx)}</span>
                  <Radio checked={isRightAnswer} disabled color="primary" style={{ padding: 6 }} />
                  <Typography
                    variant="body2"
                    style={{
                      fontWeight: isRightAnswer ? 700 : 500,
                      color: isRightAnswer ? "#065F46" : "#334155",
                      marginLeft: 8,
                    }}
                  >
                    {answerText} {isRightAnswer ? " (Đáp án đúng ✓)" : ""}
                  </Typography>
                </div>
              );
            }

            // mode === "test"
            return (
              <div
                key={optionIdx}
                className={clsx(classes.optionRow, isTestSelected && classes.optionRowTestSelected)}
                onClick={() => onSelectTestAnswer && onSelectTestAnswer(optionIdx)}
              >
                <span className={classes.optionBadge}>{getOptionLetter(optionIdx)}</span>
                <Radio checked={isTestSelected} color="primary" style={{ padding: 6 }} />
                <Typography
                  variant="body2"
                  style={{
                    fontWeight: isTestSelected ? 700 : 500,
                    color: isTestSelected ? "#0369A1" : "#334155",
                    marginLeft: 8,
                  }}
                >
                  {answerText}
                </Typography>
              </div>
            );
          })}
        </RadioGroup>
      )}

      {mode === "edit" && !isEssay && onAddAnswer && (
        <Button
          className={classes.btnAddAnswer}
          startIcon={<AddCircleOutlineIcon />}
          onClick={onAddAnswer}
        >
          Thêm lựa chọn đáp án
        </Button>
      )}
    </Paper>
  );
}
